import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Eye, EyeOff, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getPortalGetPortalUsersQueryKey,
  usePortalGetPortalUsers,
  usePortalPostPortalUsers,
} from '@/lib/api/portal/portal';
import {
  AllowedEnvironmentsField,
  toAllowedEnvironmentsArray,
  type AllowedEnvironmentsValue,
} from '@/components/AllowedEnvironmentsField';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHasClientRole } from '@/lib/portal-auth/usePortalAuth';

const InviteSchema = z.object({
  email: z.string().email().max(320),
  fullName: z.string().max(200).optional().or(z.literal('')),
  role: z.enum(['ClientViewer', 'ClientAdmin']),
});
type InviteForm = z.infer<typeof InviteSchema>;

export default function PortalUsersPage() {
  const canInvite = useHasClientRole('ClientAdmin');
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data, isLoading } = usePortalGetPortalUsers({ limit: 25 });

  return (
    <section className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">People at your client who can sign in to this portal.</p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Invite user
          </Button>
        )}
      </header>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No users yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-3 font-medium">Name</th>
                  <th className="p-3 font-medium">Email</th>
                  <th className="p-3 font-medium">Role</th>
                  <th className="p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((u) => (
                  <tr key={u.id} className="hover:bg-muted/30">
                    <td className="p-3 font-medium">{u.fullName || '—'}</td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">{u.email}</td>
                    <td className="p-3">
                      <Badge variant={u.role === 'ClientAdmin' ? 'default' : 'secondary'}>{u.role}</Badge>
                    </td>
                    <td className="p-3">
                      {u.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Disabled</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {canInvite && <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />}
    </section>
  );
}

function InviteUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const queryClient = useQueryClient();
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);
  // Portal-invited users default to Production-only (sandbox is opt-in by
  // re-invite, matching the migration policy for existing rows).
  const [allowedEnv, setAllowedEnv] = useState<AllowedEnvironmentsValue>({
    production: true,
    sandbox: false,
  });
  const [allowedEnvError, setAllowedEnvError] = useState<string | null>(null);

  const mutation = usePortalPostPortalUsers({
    mutation: {
      onSuccess: async (result) => {
        setTempPassword(result.temporaryPassword);
        await queryClient.invalidateQueries({ queryKey: getPortalGetPortalUsersQueryKey() });
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteForm>({
    resolver: zodResolver(InviteSchema),
    defaultValues: { email: '', fullName: '', role: 'ClientViewer' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!allowedEnv.production && !allowedEnv.sandbox) {
      setAllowedEnvError('Pick at least one environment.');
      return;
    }
    setAllowedEnvError(null);
    try {
      // Phase B will extend the portal invite payload — pass the array now
      // so the wire shape is ready when OpenAPI regenerates. Today it's a
      // noop the backend will ignore.
      const payload = {
        email: values.email,
        fullName: values.fullName || null,
        role: values.role,
        allowedEnvironments: toAllowedEnvironmentsArray(allowedEnv),
      };
      await mutation.mutateAsync({ data: payload as unknown as Parameters<typeof mutation.mutateAsync>[0]['data'] });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'That email already exists at this client.' : 'Could not invite user.');
    }
  });

  const close = () => {
    setTempPassword(null);
    setSecretVisible(false);
    setCopied(false);
    setAck(false);
    reset();
    onOpenChange(false);
  };

  const copy = async () => {
    if (!tempPassword) return;
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && tempPassword && !ack) return;
        if (!next) close();
      }}
    >
      <DialogContent>
        {!tempPassword ? (
          <>
            <DialogHeader>
              <DialogTitle>Invite user</DialogTitle>
              <DialogDescription>
                Invites a portal user under your client. The temp password is shown ONCE after creation.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="off" {...register('email')} />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="fullName">Full name (optional)</Label>
                <Input id="fullName" {...register('fullName')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  {...register('role')}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                >
                  <option value="ClientViewer">ClientViewer</option>
                  <option value="ClientAdmin">ClientAdmin</option>
                </select>
              </div>
              <AllowedEnvironmentsField
                value={allowedEnv}
                onChange={(next) => {
                  setAllowedEnv(next);
                  if (next.production || next.sandbox) setAllowedEnvError(null);
                }}
                description="Sandbox is opt-in for portal users. Tick it only when this teammate needs to test integrations."
                error={allowedEnvError ?? undefined}
              />
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting || mutation.isPending}>
                  {mutation.isPending ? 'Inviting…' : 'Invite user'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Save this password — it won't be shown again</DialogTitle>
              <DialogDescription>
                Share it with the invitee over a secure channel. They must change it on first sign-in.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted p-3">
                <code className="break-all font-mono text-xs">
                  {secretVisible ? tempPassword : '•'.repeat(tempPassword.length)}
                </code>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={secretVisible ? 'Hide' : 'Show'}
                    onClick={() => setSecretVisible((v) => !v)}
                  >
                    {secretVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button type="button" size="icon" variant="ghost" aria-label="Copy" onClick={copy}>
                    {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                I've delivered the password to the invitee.
              </label>
            </div>
            <DialogFooter>
              <Button type="button" onClick={close} disabled={!ack}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
